import { handleErrors, type ErrorHandlers } from '@/lib/graphql/apiErrors'
import type { FieldValues } from '@/ui/form/Form.client'
import { useCallback, useRef, useState, useTransition } from 'react'
import type { Actions, AnyActionProps } from '../drawer.types'

type UseDrawerActionsParams = {
  close: () => void
  onActionSucceeded: () => void
  onActionFailed: ErrorHandlers
}

export function useDrawerActions({
  close,
  onActionSucceeded,
  onActionFailed,
}: UseDrawerActionsParams) {
  const announcementRef = useRef<HTMLDivElement>(null)
  const [isCustomPending, startTransition] = useTransition()

  const [actions, updateActions] = useState<Actions>({
    submit: null,
    custom: [],
    cancel: null,
  })

  const announceSuccessMessage = useCallback((message: string | (() => string) | undefined) => {
    if (!message || !announcementRef.current) return

    announcementRef.current.textContent = typeof message === 'function' ? message() : message
  }, [])

  const onSubmit = useCallback(
    (data: FieldValues) => {
      startTransition(async () => {
        if (actions.submit?.action) {
          const errors = await actions.submit.action(data)

          handleErrors(errors ?? undefined, {
            onError: onActionFailed,
            onSuccess: () => {
              announceSuccessMessage(actions.submit?.validationMessages)
              onActionSucceeded()
              close()
            },
          })
        }
      })
    },
    [actions.submit, announceSuccessMessage, close, onActionFailed, onActionSucceeded],
  )

  const registerAction = useCallback(<T extends FieldValues>(action: AnyActionProps<T>) => {
    updateActions((prev) => {
      if (action.type === 'custom') {
        return {
          ...prev,
          custom: [...prev.custom.filter((item) => item.handle !== action.handle), action],
        }
      }

      return { ...prev, [action.type]: action }
    })
  }, [])

  const unregisterAction = useCallback(<T extends FieldValues>(action: AnyActionProps<T>) => {
    updateActions((prev) => {
      if (action.type === 'custom') {
        return { ...prev, custom: prev.custom.filter((item) => item.handle !== action.handle) }
      }

      return { ...prev, [action.type]: null }
    })
  }, [])

  const loading = isCustomPending
  const hasActions = actions.custom.length > 0 || !!actions.cancel || !!actions.submit

  return {
    actions,
    announcementRef,
    hasActions,
    loading,
    onSubmit,
    registerAction,
    startTransition,
    unregisterAction,
  }
}
